import { Controller } from "@hotwired/stimulus"
import { viewerTimeZone } from "../lib/viewer_time_zone"

// Live remaining time on a hold card. The job fires on send_at; this is
// display only. The visible text updates every second; the live region is
// not re-announced on each tick.
//
// Broadcasts render one card for every open tab and have no current_user.
// Author vs teammate is resolved here from the thread's viewer id, the same
// way typers and presence hide the viewer's own chip.
//
// A failed hold has no clock left to run: its copy is set once, and Undo
// becomes Dismiss. The reason is Gmail's refusal addressed to the author, so
// a teammate does not see it.
export default class extends Controller {
  static targets = ["copy", "undo", "reason"]
  static values = {
    sendAt: String,
    countdownHorizon: { type: Number, default: 3600 },
    failed: { type: Boolean, default: false },
    authorId: Number,
    authorName: { type: String, default: "" }
  }

  connect() {
    this.syncAuthorChrome()
    this.tick()
    if (this.failedValue) return

    this.timer = window.setInterval(() => this.tick(), 1000)
  }

  disconnect() {
    window.clearInterval(this.timer)
  }

  syncAuthorChrome() {
    const author = this.isAuthor()
    if (this.hasUndoTarget) this.undoTarget.hidden = !author
    if (this.hasReasonTarget) this.reasonTarget.hidden = !author
  }

  tick() {
    if (!this.hasCopyTarget) return

    this.copyTarget.textContent = this.statusCopy(this.remainingSeconds())
  }

  remainingSeconds() {
    const sendAt = Date.parse(this.sendAtValue)
    if (Number.isNaN(sendAt)) return 0

    return Math.max(0, Math.floor((sendAt - Date.now()) / 1000))
  }

  statusCopy(seconds) {
    const name = this.authorNameValue || "Someone"
    if (this.failedValue) {
      return this.isAuthor() ? "Not sent — your reply is back in the composer" : `${name}'s reply wasn't sent`
    }

    const remaining = this.remainingLabel(seconds)
    if (this.isAuthor()) return remaining

    if (remaining.startsWith("Scheduled ")) {
      return `${name} ${remaining.replace(/^Scheduled /, "scheduled ")}`
    }

    return `${name} is sending ${remaining.replace(/^Sending /, "")}`
  }

  remainingLabel(seconds) {
    if (seconds <= 0) return "Sending…"
    if (seconds < 60) return `Sending in ${seconds} seconds`
    if (seconds >= this.countdownHorizonValue) return this.scheduledForLabel()

    const minutes = Math.floor(seconds / 60)
    const leftover = seconds % 60
    return `Sending in ${minutes}:${String(leftover).padStart(2, "0")}`
  }

  scheduledForLabel() {
    const sendAt = Date.parse(this.sendAtValue)
    if (Number.isNaN(sendAt)) return "Scheduled"

    const formatted = new Intl.DateTimeFormat("en-US", {
      timeZone: viewerTimeZone(),
      month: "short",
      day: "numeric",
      year: "numeric",
      hour: "numeric",
      minute: "2-digit",
      timeZoneName: "short"
    }).format(new Date(sendAt))

    return `Scheduled for ${formatted}`
  }

  isAuthor() {
    const thread = this.element.closest("[data-thread-presence-user-id-value]")
    if (thread && this.hasAuthorIdValue) {
      return String(this.authorIdValue) === thread.dataset.threadPresenceUserIdValue
    }

    return true
  }
}
